import { CHANNEL, ENVELOPE_KINDS } from "./constants.js";
import type { Envelope, EnvelopeKind } from "./types.js";

const KIND_SET = new Set<string>(ENVELOPE_KINDS);

/** Type guard: is `value` one of the known {@link EnvelopeKind}s? */
export function isEnvelopeKind(value: unknown): value is EnvelopeKind {
  return typeof value === "string" && KIND_SET.has(value);
}

/**
 * Structural validation of an inbound `postMessage` payload.
 *
 * Returns `true` only for objects that carry our exact `channel`, a numeric
 * `protocol_version`, a known `kind`, a string `component_id`, and — where present
 * — string-typed `type` / `id` / `error`. Anything else (foreign traffic, spoofed
 * or malformed frames) fails the guard and is ignored by the caller.
 */
export function isEnvelope(value: unknown): value is Envelope {
  if (typeof value !== "object" || value === null) return false;
  const e = value as Record<string, unknown>;
  if (e.channel !== CHANNEL) return false;
  if (typeof e.protocol_version !== "number" || !Number.isFinite(e.protocol_version)) {
    return false;
  }
  if (!isEnvelopeKind(e.kind)) return false;
  if (typeof e.component_id !== "string") return false;
  if (e.type !== undefined && typeof e.type !== "string") return false;
  if (e.id !== undefined && typeof e.id !== "string") return false;
  if (e.error !== undefined && typeof e.error !== "string") return false;
  return true;
}

/**
 * Parse an arbitrary `postMessage` payload into an {@link Envelope}, or `null` if
 * it is not a valid frame for our channel.
 */
export function parseEnvelope(value: unknown): Envelope | null {
  return isEnvelope(value) ? value : null;
}

/** Fields the caller supplies when building an outbound envelope. */
export type OutboundEnvelope = Partial<Omit<Envelope, "channel">> &
  Pick<Envelope, "kind">;

/**
 * Build a fully-formed {@link Envelope}, filling in the constant `channel` and the
 * supplied protocol version / component id defaults. Only defined optional fields
 * are included so frames stay minimal and JSON-clean.
 */
export function buildEnvelope(
  fields: OutboundEnvelope,
  defaults: { protocolVersion: number; componentId: string },
): Envelope {
  const envelope: Envelope = {
    channel: CHANNEL,
    protocol_version: fields.protocol_version ?? defaults.protocolVersion,
    kind: fields.kind,
    component_id: fields.component_id ?? defaults.componentId,
  };
  if (fields.type !== undefined) envelope.type = fields.type;
  if (fields.id !== undefined) envelope.id = fields.id;
  if (fields.payload !== undefined) envelope.payload = fields.payload;
  if (fields.error !== undefined) envelope.error = fields.error;
  return envelope;
}
